import { useEffect, useState } from "react";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Eye, Users, FileText, Star, Loader2, TrendingUp, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { COMMUNE_BY_SLUG } from "@/data/communes";

const PAGE_LABELS = {
  "/": "Accueil",
  "/services": "Nos services",
  "/parcs-jardins": "Parcs & Jardins",
  "/nettoyage-exterieur": "Nettoyage extérieur",
  "/petites-reparations": "Petites réparations",
  "/a-propos": "À propos",
  "/zone-intervention": "Zone d'intervention",
  "/contact": "Contact / Devis",
  "/mentions-legales": "Mentions légales",
  "/confidentialite": "Confidentialité",
};

const pageLabel = (path) => {
  if (PAGE_LABELS[path]) return PAGE_LABELS[path];
  if (path.startsWith("/zone/")) {
    const slug = path.slice(6);
    return `Commune — ${COMMUNE_BY_SLUG[slug] || slug}`;
  }
  return path;
};

const PERIODS = [
  { key: "day", label: "Jour" },
  { key: "week", label: "Semaine" },
  { key: "month", label: "Mois" },
  { key: "year", label: "Année" },
];

const FOREST = "#1E3F2D";
const OLIVE = "#738259";

const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-white border border-line rounded-2xl p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-ink-soft text-sm">
      <Icon size={16} className="text-olive" /> {label}
    </div>
    <p className="mt-2 font-display text-3xl text-forest">{value}</p>
    {sub && <p className="text-xs text-ink-soft mt-1">{sub}</p>}
  </div>
);

const ChartCard = ({ title, total, children }) => (
  <div className="bg-white border border-line rounded-2xl p-5">
    <div className="flex items-center justify-between">
      <h3 className="font-semibold text-forest">{title}</h3>
      <span className="text-sm text-ink-soft">Total période : <strong className="text-forest">{total}</strong></span>
    </div>
    <div className="mt-4 h-64">
      <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
    </div>
  </div>
);

export const StatsPanel = () => {
  const [granularity, setGranularity] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/stats?granularity=${granularity}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [granularity]);

  const rows = data
    ? data.labels.map((label, i) => ({
        label,
        pageviews: data.series.pageviews[i],
        visitors: data.series.visitors[i],
        quotes: data.series.quotes[i],
        reviews: data.series.reviews[i],
      }))
    : [];

  const at = data?.all_time || {};
  const tt = data?.totals || {};

  // Fusion visites (par slug) + devis (par nom) pour le classement des communes
  const topCommunes = (() => {
    if (!data) return [];
    const map = {};
    (data.top_communes_visits || []).forEach(({ slug, count }) => {
      const name = COMMUNE_BY_SLUG[slug] || slug;
      map[name] = map[name] || { name, visits: 0, quotes: 0 };
      map[name].visits += count;
    });
    (data.top_communes_quotes || []).forEach(({ commune, count }) => {
      const name = commune;
      map[name] = map[name] || { name, visits: 0, quotes: 0 };
      map[name].quotes += count;
    });
    return Object.values(map)
      .sort((a, b) => b.visits + b.quotes * 3 - (a.visits + a.quotes * 3))
      .slice(0, 12);
  })();

  const sendWeekly = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post("/admin/send-weekly-report");
      toast.success("Résumé envoyé à " + (res?.to || "votre e-mail"));
    } catch {
      toast.error("Envoi impossible");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-stats">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-2xl text-forest">Statistiques du site</h2>
          <p className="text-sm text-ink-soft">Suivez la fréquentation, les demandes de devis et les avis.</p>
        </div>
        <button
          onClick={sendWeekly}
          disabled={sending}
          data-testid="send-weekly-btn"
          className="inline-flex items-center gap-2 rounded-full bg-olive text-white px-5 py-2.5 text-sm font-semibold hover:bg-olive/90 disabled:opacity-60 transition-all self-start"
        >
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
          Recevoir le résumé maintenant
        </button>
      </div>
      {/* KPI tout temps */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Kpi icon={Eye} label="Pages vues (total)" value={at.pageviews ?? "—"} testid="kpi-pageviews" />
        <Kpi icon={Users} label="Visiteurs uniques" value={at.visitors ?? "—"} testid="kpi-visitors" />
        <Kpi icon={FileText} label="Demandes de devis" value={at.quotes ?? "—"} testid="kpi-quotes" />
        <Kpi icon={TrendingUp} label="Taux de conversion" value={at.conversion != null ? `${at.conversion} %` : "—"} sub="visiteurs → devis" testid="kpi-conversion" />
        <Kpi icon={Star} label="Avis" value={at.reviews ?? "—"} sub={`${at.reviews_approved ?? 0} publié(s) · ${at.reviews_pending ?? 0} en attente`} testid="kpi-reviews" />
      </div>

      {/* Sélecteur de période */}
      <div className="mt-8 flex items-center gap-2" data-testid="period-selector">
        <span className="text-sm text-ink-soft mr-1">Vue :</span>
        {PERIODS.map((p) => (
          <button
            key={p.key}
            data-testid={`period-${p.key}`}
            onClick={() => setGranularity(p.key)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${granularity === p.key ? "bg-forest text-sand" : "bg-white border border-line text-ink-soft hover:border-olive"}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10 flex items-center gap-2 text-ink-soft"><Loader2 className="animate-spin" size={18} /> Chargement des statistiques…</div>
      ) : !data ? (
        <p className="mt-10 text-ink-soft">Impossible de charger les statistiques.</p>
      ) : (
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <ChartCard title="Fréquentation du site" total={`${tt.pageviews} vues · ${tt.visitors} visiteurs`}>
            <AreaChart data={rows} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="gPv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={OLIVE} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={OLIVE} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DF" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6B6F69" }} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6B6F69" }} />
              <Tooltip />
              <Area type="monotone" dataKey="pageviews" name="Pages vues" stroke={OLIVE} strokeWidth={2} fill="url(#gPv)" />
              <Area type="monotone" dataKey="visitors" name="Visiteurs" stroke={FOREST} strokeWidth={2} fill="transparent" />
            </AreaChart>
          </ChartCard>

          <ChartCard title="Demandes de devis" total={tt.quotes}>
            <BarChart data={rows} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DF" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6B6F69" }} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6B6F69" }} />
              <Tooltip />
              <Bar dataKey="quotes" name="Demandes" fill={OLIVE} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ChartCard>

          <ChartCard title="Avis reçus" total={tt.reviews}>
            <BarChart data={rows} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DF" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6B6F69" }} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6B6F69" }} />
              <Tooltip />
              <Bar dataKey="reviews" name="Avis" fill={FOREST} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ChartCard>

          <div className="bg-white border border-line rounded-2xl p-5" data-testid="top-pages">
            <h3 className="font-semibold text-forest">Pages les plus visitées</h3>
            <p className="text-xs text-ink-soft mt-0.5">Classement tout temps</p>
            <div className="mt-4 space-y-3">
              {(data.top_pages || []).length === 0 && <p className="text-sm text-ink-soft">Aucune visite enregistrée pour le moment.</p>}
              {(data.top_pages || []).map((p, i) => {
                const max = data.top_pages[0].count || 1;
                return (
                  <div key={p.path} className="text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-forest truncate"><span className="text-ink-soft mr-2">{i + 1}.</span>{pageLabel(p.path)}</span>
                      <span className="text-ink-soft shrink-0 font-semibold">{p.count}</span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-sand overflow-hidden">
                      <div className="h-full bg-olive rounded-full transition-all" style={{ width: `${Math.round((p.count / max) * 100)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white border border-line rounded-2xl p-5 lg:col-span-2" data-testid="top-communes">
            <h3 className="font-semibold text-forest">Communes les plus populaires</h3>
            <p className="text-xs text-ink-soft mt-0.5">Classement tout temps — visites des pages communes & demandes de devis</p>
            {topCommunes.length === 0 ? (
              <p className="mt-4 text-sm text-ink-soft">Aucune donnée par commune pour le moment.</p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-ink-soft border-b border-line">
                      <th className="py-2 font-medium">Commune</th>
                      <th className="py-2 font-medium text-right w-24">Visites</th>
                      <th className="py-2 font-medium text-right w-24">Devis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topCommunes.map((c, i) => (
                      <tr key={c.name} className="border-b border-line/60 last:border-0">
                        <td className="py-2.5 text-forest"><span className="text-ink-soft mr-2">{i + 1}.</span>{c.name}</td>
                        <td className="py-2.5 text-right font-semibold text-forest">{c.visits}</td>
                        <td className="py-2.5 text-right font-semibold text-olive">{c.quotes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
